(function (globalScope) {
  'use strict';

  function includesValue(values, value) {
    return Array.isArray(values) && values.includes(value);
  }

  function splitCsv(value) {
    if (Array.isArray(value)) return value.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
    return String(value || '').split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
  }

  function normalizeMappings(mappings) {
    if (!Array.isArray(mappings)) return [];
    return mappings.map((mapping) => ({
      ...mapping,
      bases: splitCsv(mapping.bases || mapping.basesCsv),
      styles: splitCsv(mapping.styles || mapping.stylesCsv),
      moods: splitCsv(mapping.moods || mapping.moodsCsv),
    }));
  }

  function scoreAnswers(answers, mappings) {
    if (!answers || !answers.base || !answers.style || !answers.mood) return null;
    if (!Array.isArray(mappings)) return null;

    let winner = null;
    let winnerScore = -1;

    mappings.forEach((mapping) => {
      if (!mapping || mapping.available === false) return;

      const score =
        (includesValue(mapping.bases, answers.base) ? 4 : 0) +
        (includesValue(mapping.styles, answers.style) ? 3 : 0) +
        (includesValue(mapping.moods, answers.mood) ? 2 : 0);

      if (score > winnerScore) {
        winner = mapping;
        winnerScore = score;
      }
    });

    return winner;
  }

  function buildCartPayload(mapping) {
    if (!mapping || !mapping.available || mapping.purchasable !== true || !mapping.variantId) return null;
    return { items: [{ id: mapping.variantId, quantity: 1 }] };
  }

  function nextStep(currentStep) {
    return currentStep >= 2 ? 'result' : currentStep + 1;
  }

  function createResultModel(mapping) {
    if (!mapping) return null;
    const cartPayload = buildCartPayload(mapping);
    return {
      title: mapping.title || 'Your flavor match',
      description: mapping.description || '',
      image: mapping.image || '',
      accent: mapping.accent || '#F52D78',
      url: mapping.url || '/collections/all',
      action: cartPayload ? 'cart' : 'link',
      label: cartPayload ? 'ADD MY MATCH' : 'SHOP MY MATCH',
      cartPayload,
    };
  }

  function makeElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  }

  function initSection(root) {
    if (!root || root.dataset.ctcEnhanced === 'true') return false;
    const quiz = root.querySelector('[data-ctc-quiz]');
    const mappingNode = root.querySelector('[data-ctc-mappings]');
    if (!quiz || !mappingNode) return false;

    let mappings = [];
    try { mappings = normalizeMappings(JSON.parse(mappingNode.textContent)); } catch (error) { mappings = []; }

    root.dataset.ctcEnhanced = 'true';
    const answers = { base: '', style: '', mood: '' };
    const steps = Array.from(quiz.querySelectorAll('[data-quiz-step]'));
    const progress = Array.from(root.querySelectorAll('[data-progress-step]'));
    const result = quiz.querySelector('[data-quiz-result]');
    let activeStep = 0;

    function showStep(index) {
      activeStep = index;
      steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === index));
      progress.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
      const firstChoice = steps[index] && steps[index].querySelector('[data-answer-name]');
      if (firstChoice) firstChoice.focus({ preventScroll: true });
    }

    function renderResult(mapping) {
      const model = createResultModel(mapping);
      steps.forEach((step) => step.classList.remove('is-active'));
      progress.forEach((item) => item.classList.add('is-active'));
      result.replaceChildren();
      result.hidden = false;
      result.style.setProperty('--ctc-result-accent', model ? model.accent : '#F52D78');

      if (!model) {
        result.append(makeElement('p', '', 'Choose any flavor that fits your night.'));
        const fallback = makeElement('a', 'ctc-button ctc-button--pink', 'SHOP ALL FLAVORS →');
        fallback.href = '/collections/all';
        result.append(fallback);
        return;
      }

      const media = model.image ? makeElement('img', 'ctc-result__image') : makeElement('div', 'ctc-result__placeholder', model.title);
      if (model.image) { media.src = model.image; media.alt = model.title; }
      const copy = makeElement('div', 'ctc-result__copy');
      copy.append(makeElement('p', 'ctc-kicker ctc-kicker--dark', 'YOUR MATCH'));
      copy.append(makeElement('h3', 'ctc-brush', model.title));
      if (model.description) copy.append(makeElement('p', '', model.description));
      const actions = makeElement('div', 'ctc-result__actions');

      if (model.action === 'cart') {
        const addButton = makeElement('button', 'ctc-button ctc-button--pink', model.label + ' →');
        addButton.type = 'button';
        addButton.addEventListener('click', async () => {
          addButton.disabled = true;
          addButton.textContent = 'ADDING…';
          try {
            const rootPath = globalScope.Shopify && globalScope.Shopify.routes ? globalScope.Shopify.routes.root : '/';
            const response = await fetch(rootPath + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(model.cartPayload),
            });
            if (!response.ok) throw new Error('Cart request failed');
            globalScope.location.assign(rootPath + 'cart');
          } catch (error) {
            addButton.disabled = false;
            addButton.textContent = 'VIEW MY MATCH →';
            addButton.addEventListener('click', () => globalScope.location.assign(model.url), { once: true });
          }
        });
        actions.append(addButton);
      } else {
        const productLink = makeElement('a', 'ctc-button ctc-button--pink', model.label + ' →');
        productLink.href = model.url;
        actions.append(productLink);
      }

      const restart = makeElement('button', 'ctc-quiz__restart', 'START OVER');
      restart.type = 'button';
      restart.addEventListener('click', () => {
        answers.base = ''; answers.style = ''; answers.mood = '';
        quiz.querySelectorAll('.is-selected').forEach((choice) => choice.classList.remove('is-selected'));
        quiz.querySelectorAll('[data-quiz-next]').forEach((button) => { button.disabled = true; });
        result.hidden = true;
        showStep(0);
      });
      actions.append(restart);
      copy.append(actions);
      result.append(media, copy);
      result.focus({ preventScroll: true });
    }

    quiz.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-answer-name]');
      if (choice) {
        const name = choice.dataset.answerName;
        answers[name] = choice.dataset.answerValue;
        choice.closest('[data-quiz-step]').querySelectorAll('[data-answer-name]').forEach((item) => {
          const selected = item === choice;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        choice.closest('[data-quiz-step]').querySelector('[data-quiz-next]').disabled = false;
        return;
      }

      const nextButton = event.target.closest('[data-quiz-next]');
      if (!nextButton || nextButton.disabled) return;
      const destination = nextStep(activeStep);
      if (destination === 'result') renderResult(scoreAnswers(answers, mappings));
      else showStep(destination);
    });

    showStep(0);
    return true;
  }

  const api = { buildCartPayload, createResultModel, initSection, nextStep, normalizeMappings, scoreAnswers };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  globalScope.CTCHome = Object.assign(globalScope.CTCHome || {}, api);

  if (typeof document !== 'undefined') {
    const initAll = (scope) => scope.querySelectorAll('[data-ctc-home]').forEach(initSection);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
    else initAll(document);
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})(typeof window !== 'undefined' ? window : globalThis);
